import React, {useEffect} from 'react';
import {StyleSheet, View, ViewStyle} from 'react-native';
import {Button, TextInput} from 'ui-kits';
import {StoryTabs} from './StoryTabs';
import {spacing} from 'theme/Units';
import {userStoryFormValuesType} from './userStoryFormValuesType';
import {Formik, FormikProps} from 'formik';
import {QuickAddUserStoryValidation} from 'utils/schema';
import {selectedProjectData} from 'redux/selectors/projectSelector';
import {useSelector} from 'react-redux';
import {useAuthAccess} from 'hooks';

type propTypes = Readonly<{
  mode: 'add' | 'edit' | 'view';
  initialValues?: userStoryFormValuesType;
  onSubmit: (item: userStoryFormValuesType) => void;
  onCancel: () => void;
}>;

const initialValues: userStoryFormValuesType = {
  title: '',
  description: '',
  followers: [],
  priority: 0,
  proj_id: null,
  responsible: null,
  status: 1,
  due_date: null,
  tags: [],
};

export function StoryDetailsForm(props: propTypes) {
  const {
    mode = 'add',
    onSubmit,
    initialValues: initialValuesProps,
    onCancel,
  } = props;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={QuickAddUserStoryValidation}
      onSubmit={onSubmit}>
      {formkiProps => {
        return (
          <StoryForm
            mode={mode}
            initialValuesProps={initialValuesProps}
            formikProps={formkiProps}
            onCancel={onCancel}
          />
        );
      }}
    </Formik>
  );
}

type StoryFormPropType = Readonly<{
  mode?: propTypes['mode'];
  initialValuesProps?: userStoryFormValuesType;
  onCancel?: () => void;
  formikProps: FormikProps<userStoryFormValuesType>;
}>;

function StoryForm(props: StoryFormPropType) {
  const {mode, onCancel, formikProps, initialValuesProps} = props;
  const selectedProject = useSelector(selectedProjectData);
  const {setValues} = formikProps;
  const {hasWriteAccess} = useAuthAccess();

  useEffect(() => {
    if (initialValuesProps && mode === 'view') {
      // de-serialize the values
      setValues(initialValuesProps);
    }
  }, [initialValuesProps, setValues, mode]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {mode === 'view' ? (
          <View>
            <TextInput
              label="ID"
              placeholder="project ID"
              value={initialValuesProps?.us_id}
              disabled
            />
          </View>
        ) : null}
        <View style={styles.titleInputWrapper}>
          <TextInput
            label="Title"
            required
            placeholder="Story title"
            error={formikProps.errors.title}
            value={formikProps.values.title}
            onChangeText={formikProps.handleChange('title')}
            onBlur={formikProps.handleBlur('title')}
          />
        </View>
        <View>
          <TextInput
            label="Project"
            disabled
            value={initialValuesProps?.project?.name ?? selectedProject?.name}
            placeholder="project ID"
          />
        </View>
        {hasWriteAccess ? (
          <View style={styles.buttonContainer}>
            <Button
              style={styles.button}
              text="Cancel"
              variant="secondary"
              onPress={onCancel}
            />
            <Button
              style={styles.button}
              text={mode === 'view' ? 'Save' : 'Add'}
              disabled={
                mode === 'add'
                  ? !formikProps.isValid || !formikProps.dirty
                  : !formikProps.isValid
              }
              onPress={formikProps.handleSubmit}
            />
          </View>
        ) : null}
      </View>
      <StoryTabs {...formikProps} mode={mode as any} />
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  header: ViewStyle;
  button: ViewStyle;
  titleInputWrapper: ViewStyle;
  buttonContainer: ViewStyle;
};
const styles: styleType = StyleSheet.create({
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing['2'],
  },
  container: {
    gap: spacing['4'],
    flex: 1,
  },
  titleInputWrapper: {
    flex: 1,
    marginRight: 'auto',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing['2'],
  },
  button: {
    width: 'fit-content',
    paddingHorizontal: spacing['4'],
  },
});
